/**
 * The connected domino chain on the ahwa table felt.
 *
 * Rendering model: one "world" div holds every placed tile at fixed world
 * coordinates (the chain layout is stable — existing tiles never move in
 * world space, and the round's opening tile always sits at the exact table
 * center). The whole world is fitted to the container with a single
 * translate+scale transform ANCHORED on the opening tile: the camera zooms
 * about that fixed pivot as the arms grow, so the opener never drifts off
 * the horizontal center and placed tiles only ever scale in place (the view
 * recenters vertically only when an end turns a corner and grows downward).
 *
 * New placements fly in from their source seat with an arc, landing bounce,
 * impact ripple and (for doubles) a table shake — see lib/physics.ts.
 */

import type { Chain, End } from "@domino/types";
import { useCallback, useEffect, useRef, useState } from "react";

import { impactRipple, landBounce, flyTile } from "../lib/physics";
import type { TableDimensions } from "../scene/layoutTypes";
import {
  computeAnchoredFitTransform,
  computeChainLayout,
  computeFitTransform,
  computeGhostPlacement,
  TILE_LENGTH,
  TILE_WIDTH,
} from "../scene/layout";
import { TileFace } from "./tiles";

export type FlightDir = "bottom" | "top" | "left" | "right" | "yard";

export interface BoardFlight {
  /** Tile that should fly in (it mounts as a new placement). */
  tileId: number;
  dir: FlightDir;
  /** Increments each play so identical tiles re-trigger. */
  nonce: number;
}

export interface BoardProps {
  chain: Chain;
  activeEnds?: End[];
  dragOverEnd?: End | null;
  onChooseEnd?: (end: End) => void;
  isDragging?: boolean;
  flight?: BoardFlight | null;
  /** Fired when a tile's flight lands (or immediately without animation). */
  onLanded?: (tileId: number, isDouble: boolean) => void;
}

/** Pixels per world unit at zoom 1. Tile = 80x40 px in world space. */
const BASE = 40;

function tableDimsFor(aspect: number): TableDimensions {
  if (aspect >= 1.45) return { width: 36, height: 24, margin: 2 };
  if (aspect >= 0.9) return { width: 30, height: 26, margin: 1.8 };
  return { width: 24, height: 30, margin: 1.6 };
}

function sourcePointFor(dir: FlightDir, table: TableDimensions): { x: number; y: number } {
  const cx = table.width / 2;
  const cy = table.height / 2;
  switch (dir) {
    case "bottom":
      return { x: cx, y: table.height + 2.5 };
    case "top":
      return { x: cx, y: -2.5 };
    case "left":
      return { x: -2.5, y: cy };
    case "right":
      return { x: table.width + 2.5, y: cy };
    case "yard":
      return { x: 2.4, y: table.height - 2.2 };
  }
}

export function Board({
  chain,
  activeEnds = [],
  dragOverEnd = null,
  onChooseEnd,
  isDragging = false,
  flight = null,
  onLanded,
}: BoardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const worldRef = useRef<HTMLDivElement | null>(null);
  const rippleLayerRef = useRef<HTMLDivElement | null>(null);
  const tileRefs = useRef(new Map<number, HTMLDivElement>());
  const mountedTiles = useRef(new Set<number>());
  const [viewportSize, setViewportSize] = useState({ width: 800, height: 440 });

  const leftActive = activeEnds.includes("left");
  const rightActive = activeEnds.includes("right");
  const centerActive = activeEnds.includes("center") || chain.placements.length === 0;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const { clientWidth, clientHeight } = el;
      if (clientWidth > 0 && clientHeight > 0) {
        setViewportSize({ width: clientWidth, height: clientHeight });
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const table = tableDimsFor(viewportSize.width / Math.max(1, viewportSize.height));
  const layout = computeChainLayout(chain, viewportSize, table, {
    minZoom: 15,
    maxZoom: 47,
  });
  // Camera: the opening tile sits at the exact table center in world space,
  // so the fit pins THAT point to the horizontal center of the viewport while
  // the pin keeps both ends visible (ADR-0027 slide-first): an overflowing
  // arm drags the opener sideways just enough to stay on the felt, and the
  // camera zooms only when even sliding cannot fit the chain. Bounds are
  // padded by a tile+margin on every side so the open-end targets (one tile
  // beyond the chain) never clip. On small viewports the roomy pad would
  // force the zoom below its floor and clip edges, so fall back to a tighter
  // pad there.
  const anchor = { x: table.width / 2, y: table.height / 2 };
  const padBounds = (pad: number) => ({
    minX: layout.bounds.minX - pad,
    minY: layout.bounds.minY - pad,
    maxX: layout.bounds.maxX + pad,
    maxY: layout.bounds.maxY + pad,
  });
  const padFit = (pad: number, minZoom: number) =>
    computeAnchoredFitTransform(padBounds(pad), anchor, viewportSize, minZoom, 40);
  const roomyFit = padFit(3.4, 15);
  const tight = roomyFit.zoom <= 16;
  let fit = tight ? padFit(2.0, 12) : roomyFit;
  // Safety valve: a pathological one-arm chain on a tiny viewport can clamp
  // at the zoom floor and overflow the felt. Visibility beats the pin — fall
  // back to plain bbox centering in that case only.
  {
    const pb = padBounds(tight ? 2.0 : 3.4);
    const overflowPx = Math.max(
      -(pb.minX * fit.zoom + fit.offsetX),
      pb.maxX * fit.zoom + fit.offsetX - viewportSize.width,
      -(pb.minY * fit.zoom + fit.offsetY),
      pb.maxY * fit.zoom + fit.offsetY - viewportSize.height,
    );
    if (overflowPx > 4) {
      fit = computeFitTransform(pb, viewportSize, tight ? 12 : 15, 40);
    }
  }
  // The world holds world*BASE px, so only the SCALE is divided by BASE.
  // The fit offset is already in screen px and must NOT be divided:
  // screen = (world*BASE) * (zoom/BASE) + offset == world*zoom + offset.
  // (.board-world uses transform-origin: 0 0 so this composes exactly.)
  const { zoom, offsetX, offsetY } = fit;
  const cssScale = zoom / BASE;
  const cssOffsetX = offsetX;
  const cssOffsetY = offsetY;

  // Remember the last fit so we can skip the world transition on first paint.
  const firstFit = useRef(true);
  useEffect(() => {
    firstFit.current = false;
  }, []);

  const announceLanding = useCallback(
    (tileId: number, wrapper: HTMLElement) => {
      const placement = chain.placements.find((p) => p.tileId === tileId);
      const isDouble = placement ? placement.inwardHalf === placement.outwardHalf : false;
      const pos = tileRefs.current.get(tileId);
      if (rippleLayerRef.current && pos) {
        impactRipple(rippleLayerRef.current, {
          x: pos.offsetLeft + pos.offsetWidth / 2,
          y: pos.offsetTop + pos.offsetHeight / 2,
        }, isDouble);
      }
      const face = wrapper.querySelector(".domino-bone");
      if (face instanceof HTMLElement) landBounce(face, isDouble);
      onLanded?.(tileId, isDouble);
    },
    [chain.placements, onLanded],
  );

  // Flight choreography for newly mounted placements.
  useEffect(() => {
    if (!flight) return;
    if (chain.placements.length === 0) return;
    const wrapper = tileRefs.current.get(flight.tileId);
    if (!wrapper) return;
    if (mountedTiles.current.has(flight.tileId)) return;
    mountedTiles.current.add(flight.tileId);

    const index = chain.placements.findIndex((p) => p.tileId === flight.tileId);
    const placed = layout.placements[index];
    if (!placed) return;

    const face = wrapper.querySelector(".domino-bone");
    if (face instanceof HTMLElement) {
      // Fly the bone itself so the wrapper rotation composes cleanly.
      const src = sourcePointFor(flight.dir, table);
      const rot = placed.rotation;
      // World delta, then un-rotate it into the wrapper's local axes.
      const wdx = (src.x - placed.x) * BASE;
      const wdy = (src.y - placed.y) * BASE;
      const cos = Math.cos(-rot);
      const sin = Math.sin(-rot);
      const localDx = wdx * cos - wdy * sin;
      const localDy = wdx * sin + wdy * cos;
      const spin = flight.dir === "yard" ? -120 : 0;
      void flyTile(face, { x: localDx, y: localDy }, { durationMs: 380, arc: -26, spinDeg: spin }).then(
        () => announceLanding(flight.tileId, wrapper),
      );
    } else {
      announceLanding(flight.tileId, wrapper);
    }
  }, [flight?.nonce, flight?.tileId, chain.placements.length]);

  // Reset mount tracking when the round resets (chain empties).
  useEffect(() => {
    if (chain.placements.length === 0) mountedTiles.current.clear();
  }, [chain.placements.length]);

  const worldStyle: React.CSSProperties = {
    transform: `translate(${cssOffsetX}px, ${cssOffsetY}px) scale(${cssScale})`,
    transition: firstFit.current ? "none" : "transform 520ms cubic-bezier(0.25, 0.9, 0.3, 1)",
  };

  // ---------------------------------------------------------------- empty
  if (chain.placements.length === 0) {
    return (
      <div className="board-area" ref={containerRef}>
        <div
          data-drop-end="center"
          className={`board-opening ${centerActive ? "opening-active" : ""} ${
            dragOverEnd === "center" ? "opening-hover" : ""
          }`}
          onClick={() => onChooseEnd?.("center")}
        >
          <div className="opening-mat">
            <span className="opening-star" aria-hidden />
            <span className="opening-title">
              {isDragging ? "اطلحها هنا · Drop it here" : "أعلى دبل يفتح · OPENING TILE"}
            </span>
            <span className="opening-sub">
              Round one leads with the highest double — الدش 6·6, الدبش 5·5…
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ full chain
  const leftEndVal = chain.leftEnd ?? 0;
  const rightEndVal = chain.rightEnd ?? 0;
  const ghostLeft = computeGhostPlacement(
    chain,
    "left",
    { high: leftEndVal, low: leftEndVal, isDouble: false },
    table,
  );
  const ghostRight = computeGhostPlacement(
    chain,
    "right",
    { high: rightEndVal, low: rightEndVal, isDouble: false },
    table,
  );
  const lastPlacement = chain.placements[chain.placements.length - 1];

  const renderGhost = (end: End) => {
    const ghost = end === "left" ? ghostLeft.left : ghostRight.right;
    if (!ghost) return null;
    const active = end === "left" ? leftActive : rightActive;
    const hovered = dragOverEnd === end;
    const grot = (ghost.rotation * 180) / Math.PI;
    return (
      <div
        key={`ghost-${end}`}
        data-drop-end={end}
        className={`board-end-target ${active ? "target-active" : ""} ${hovered ? "target-hover" : ""}`}
        style={{
          position: "absolute",
          left: `${ghost.x * BASE}px`,
          top: `${ghost.y * BASE}px`,
          width: `${ghost.width * BASE}px`,
          height: `${ghost.height * BASE}px`,
          transform: `translate(-50%, -50%) rotate(${grot}deg)`,
        }}
        onClick={() => active && onChooseEnd?.(end)}
        title={active ? `Play on ${end} (${end === "left" ? leftEndVal : rightEndVal})` : undefined}
      >
        <span className="end-glow" />
        {/* counter-rotated so numbers and labels always read upright */}
        <span className="end-upright" style={{ transform: `rotate(${-grot}deg)` }}>
          <b className="end-pip-count">{end === "left" ? leftEndVal : rightEndVal}</b>
          {active && <i className="end-callout">{hovered ? "هنا!" : "DROP"}</i>}
        </span>
      </div>
    );
  };

  return (
    <div className={`board-area ${isDragging ? "board-dragging" : ""}`} ref={containerRef}>
      <div className="board-world" ref={worldRef} style={worldStyle}>
        {/* open-end value markers (always visible) */}
        {renderGhost("left")}
        {renderGhost("right")}

        {/* placed tiles — world coordinates are stable per tileId */}
        {chain.placements.map((placement, index) => {
          const placed = layout.placements[index];
          if (!placed) return null;
          const rotDeg = (placed.rotation * 180) / Math.PI;
          const isDouble = placement.inwardHalf === placement.outwardHalf;
          return (
            <div
              key={placement.tileId}
              ref={(el) => {
                if (el) tileRefs.current.set(placement.tileId, el);
                else tileRefs.current.delete(placement.tileId);
              }}
              className={`board-tile ${isDouble ? "board-tile-double" : ""}`}
              style={{
                position: "absolute",
                left: `${placed.x * BASE}px`,
                top: `${placed.y * BASE}px`,
                width: `${TILE_LENGTH * BASE}px`,
                height: `${TILE_WIDTH * BASE}px`,
                transform: `translate(-50%, -50%) rotate(${rotDeg}deg)`,
              }}
            >
              <TileFace
                tileId={placement.tileId}
                halves={[placement.inwardHalf, placement.outwardHalf]}
                horizontal
                justPlayed={placement === lastPlacement}
                style={{ width: "100%", height: "100%" }}
                title={undefined}
              />
            </div>
          );
        })}

        {/* impact ripples live in world space so they scale with the camera */}
        <div className="board-ripple-layer" ref={rippleLayerRef} />
      </div>
    </div>
  );
}
